import dayjs from 'dayjs';
import session from './session';
import { certificationStatusCode, indentityCode } from '@/config';

import auditPassedImg from '@/assets/auditPass.png';
import auditWattingImg from '@/assets/auditing.png';
import auditRejectImg from '@/assets/error.png';

/**
 * 格式化日期
 * @param {Number | String | Date} value 
 * @param {String} format 
 */
export const formatDate = (value, format = 'YYYY-MM-DD') => {  
  return value ? dayjs(value).format(format) : '';
}

export const validIsSupplier = (identityCode = '') => {
  return [indentityCode.bid.supplier, indentityCode.cenpur.supplier].includes(identityCode);
}

export const validIsPurchaser = (identityCode = '') => {
  return [indentityCode.bid.purchaser, indentityCode.cenpur.purchaser].includes(identityCode);
}

/**
 * 建议表单校验器
 * @param {any} value 
 * @param {ValidationRule | ValidationRule[]} rule 
 * @param {ValidationValues} values 
 * @returns {String}
 */
const validate = (value, rule, values = {}) => {
  if (!rule) {
    return '';
  }
  if (typeof rule === 'string') {
    return !value ? rule : '';
  }

  if (typeof rule === 'function') {
    return rule(value, values);
  }

  if (typeof rule === 'object' && rule.test instanceof RegExp) {
    return !(rule.test.test(value)) ? rule.message : '';
  }

  if (Array.isArray(rule)) {
    return rule.reduce((prev, el) => {
      return prev || validate(value, el, values);
    }, '');
  }

  if (typeof rule === 'object' && typeof rule.validator === 'function') {
    return validate(value, rule.validator, values);
  }
  // // 如果是带有reference，则代表该字段校验必须参考reference配置的字段校验通过后才能够校验此字段，否则判定为不需要校验的对象
  // if (typeof rule === 'object' && typeof rule.reference === 'object') {
  //   return validate(values[rule.reference.field], rule.reference.rule) ? validate(value, rule) : '';
  // }
  return '';
}

export const mixins = {
  filters: {
    formatDate,
    formatInviteSteelText
  },
  methods: {
    formatDate,
    setFieldValue (schema, name) {
      const that = this;
      return function setValue (value) {
        that.$set(schema, name, value);
      };
    },
    validateFieldWithErrors (errors, rules, name) {
      const that = this;
      function validateValue(value) {
        that.$set(errors, name, validate(value, rules[name]));
      }
      return validateValue;
    },
    validateFields (values, rules) {
      let errors = {};
      for (let [field, value] of Object.entries(values)) {
        const rule = rules[field];
        errors[field] = validate(value, rule, values);
      }
      return {
        isValid: Object.entries(errors).every(([_, value]) => !value),
        errors,
      };
    },
    validateField(value, rule) {
      const errorInfo = validate(value, rule);
      return {
        error: errorInfo,
        isValid: !errorInfo,
      };
    },
    filterValueKeys (fields, keys = [], isReverse = false) {
      const result = {};
      for (let [key, value] of Object.entries(fields)) {
        const hasKey = keys.indexOf(key) !== -1;

        if (isReverse ? !hasKey : hasKey) {
          continue;
        }
        result[key] = value;
      }
      return result;
    },
    resolveCategoryIds (treeList) {
      if (!treeList.length) {
        return [];
      }
      return treeList.reduce((preValue, tree) => {
        return tree.children && tree.children.length ? preValue.concat(this.resolveCategoryIds(tree.children)) : preValue.concat(tree.id);
      }, []);
    },
    resolveOpendInfo (list) {
      // FIXME:待优化
      // 是否开通了集采供应商身份
      let supplierHasOpend = false;
      let purchaserHasOpend = false;
      const hasSupplier = !!list.filter(platform => {
        return !!platform.identity.filter(identity => {
          if (
            validIsSupplier(identity.code) &&
            (identity.status === '2' || identity.status === '1')
          ) {
            supplierHasOpend = true
          }
          return validIsSupplier(identity.code) && identity.status !== '3'
        }).length
      }).length

      // 是否开通了集采采购商身份
      const hasPurchaser = !!list.filter(platform => {
        return !!platform.identity.filter(identity => {
          if (
            validIsPurchaser(identity.code) &&
            (identity.status === '2' || identity.status === '1')
          ) {
            purchaserHasOpend = true
          }
          return validIsPurchaser(identity.code) && identity.status !== '3'
        }).length
      }).length

      return {
        hasSupplier,
        supplierHasOpend,
        hasPurchaser,
        purchaserHasOpend
      };
    },
  },
  computed: {
    certStatusImg () {
      if (!this.certification) {
        return null;
      }
      const auditStatus = this.certification.auditStatus;
      if (auditStatus === certificationStatusCode.openning) {
        return {
          img: auditWattingImg,
          text: '请等待后台审核',
          type: 'watting',
        };
      }
      if (auditStatus === certificationStatusCode.opened) {
        return {
          img: auditPassedImg,
          text: '认证通过',
          type: 'passed'
        };
      }
      if (auditStatus === certificationStatusCode.reject) {
        return {
          img: auditRejectImg,
          text: '认证失败',
          type: 'error'
        };
      }
      return null;
    },
  },
};

/**
 * 格式化受邀钢厂文字显示
 * @param {string} value 
 * @param {*} text 
 */
export function formatInviteSteelText(value, text = '自己感兴趣，无受邀钢厂') {
  return value ? value.replace(/none|###/, text) : text;
}

/**
 * 
 * @param {string} value 
 */
export function formatInviteSteelValue(value) {
  return value ? value.replace(/none|###/, '') : value;
}

export const logout = () => {
  session.removeTicket()
  session.removeToken();
}

/**
 * 替换字符串符号
 * @param {string} value 要替换的字符串
 * @param {Array<[string, string]>} matchRule 要替换的符号
 */
export function formatStringSymbol (value = '', matchRule = [['\\(', '（'], ['\\)', '）']]) {
  return matchRule.reduce((result, [source, target]) => {
    return result.replace(new RegExp(source, 'g'), target);
  }, value || '');
};

/**
 * 用于在外部获取url的pathname
 */
export function getHashUrlPathname() {
  const href = location.hash;
  return href.split('#')[1] ? href.split('#')[1].split('?')[0] || '' : '';
}

/**
 * 生成随机字符串
 */
export function genarateRandomString() {
  return (Math.random() + +new Date().getMilliseconds()).toString(16).slice(-10);
}